import { api } from '@/lib/api';
import { IAM_BASE } from './paths';
import { parseLocations, type TrLocation } from '@/lib/locations';

/**
 * IAM uclari — TEK yerde tipli sarmalayicilar.
 *
 * Zarf sozlesmesi her uc icin ayni: `{ code: 'success' | 'error', message, ... }`.
 * HTTP 200 + `code: 'error'` mumkun oldugu icin `code` HER ZAMAN kontrol edilir;
 * yalnizca HTTP durumuna bakan bir cagri sessizce basarili sanir.
 */

export type ApiEnvelope = { code: string; message?: string };

/** pg int8'i STRING dondurur; kimlikler asla sayiya cevrilmez. */
export type RawUser = {
  id: string | number;
  userFullName?: string;
  userEmail?: string;
  userPhone?: string;
  userCity?: string;
  userDistrict?: string;
  userType?: string;
  userRoleCode?: string;
  kvkkAccepted?: boolean;
  usageAgreementAccepted?: boolean;
  mustChangePassword?: boolean;
};

/**
 * Organizasyon iki AYRI SEKILDE gelir ve ikisi de burada karsilanir:
 *   * login yaniti  -> { id, name, typeCode, memberType }  (iam-service.js elle kurar)
 *   * /v1/organizations/me -> HAM organizasyon satiri: { id, organizationName,
 *     organizationTypeCode, ... }
 * Bu yuzden ad alanlarinin ikisi de opsiyonel; esleme session.store.ts'teki toOrganization'da.
 */
export type RawOrganization = {
  id: string | number;
  name?: string;
  organizationName?: string;
  typeCode?: string;
  organizationTypeCode?: string;
  memberType?: string;
};

export type AuthPayload = ApiEnvelope & {
  accessToken?: string;
  refreshToken?: string;
  refreshExpiresAt?: number;
  user?: RawUser;
  topicList?: string[];
  isExchangeProfile?: boolean;
  organization?: RawOrganization | null;
  userPermissionList?: string[];
};

export const postLogin = async (input: {
  userEmail: string;
  userPassword: string;
}): Promise<AuthPayload> => {
  const { data } = await api.post<AuthPayload>(`${IAM_BASE}/v1/login`, input);
  return data;
};

/**
 * Cikis. Erisim jetonu suresi dolmus ya da hic yokken de calisir — govdedeki
 * yenileme jetonu hangi oturumun iptal edilecegini soyler.
 */
export const postLogout = async (input: {
  refreshToken?: string | null;
  allDevices?: boolean;
}): Promise<void> => {
  await api.post(
    `${IAM_BASE}/v1/logout`,
    { refreshToken: input.refreshToken ?? undefined },
    input.allDevices ? { params: { all: 'true' } } : undefined,
  );
};

export type FirstLoginInput = {
  userPassword: string;
  userPhone: string;
  userIdentityId: string;
  userCity: string;
  userDistrict: string;
  kvkkAcknowledged: boolean;
  usageAgreementAccepted: boolean;
  legalDocVersion: string;
};

/**
 * Zorunlu ilk giris. Sunucu bu cagrida kullanicinin TUM oturumlarini dusurup
 * yeni bir jeton cifti basar — yani donen jetonlar mutlaka yerine yazilmalidir,
 * yoksa kullanici kendi kaydettigi sifreyle aninda disari atilir.
 */
export const postFirstLogin = async (input: FirstLoginInput): Promise<AuthPayload> => {
  const { data } = await api.post<AuthPayload>(`${IAM_BASE}/v1/users/first-login`, input);
  return data;
};

export const postAcceptKvkk = async (userId: string): Promise<ApiEnvelope> => {
  const { data } = await api.post<ApiEnvelope>(`${IAM_BASE}/v1/agreements/kvkk/accept`, {
    userId,
  });
  return data;
};

export const postAcceptUsage = async (userId: string): Promise<ApiEnvelope> => {
  const { data } = await api.post<ApiEnvelope>(`${IAM_BASE}/v1/agreements/usage/accept`, {
    userId,
  });
  return data;
};

/**
 * Kendi kaydi. Sunucu `userId` parametresini yoksayip cagiranin kendi satirini
 * doner (ayricalikli roller haric) — yani bu bir "ben kimim" ucu DEGIL ama en
 * yakin duran sey: profil + platform rolu + onay durumu + mustChangePassword.
 */
export type UsersResponse = ApiEnvelope & { userList?: RawUser[] };

export const getSelf = async (): Promise<RawUser | null> => {
  const { data } = await api.get<UsersResponse>(`${IAM_BASE}/v1/users`);
  if (data.code !== 'success') return null;
  return data.userList?.[0] ?? null;
};

/**
 * Organizasyon baglamı. `membership.permissions` cagiranin ETKIN yetkileridir
 * (yonetici icin sunucu ortuk yetkileri de listeye acar).
 *
 * DIKKAT: `GET /v1/permissions` BU DEGIL — o, personele VERILEBILEN kodlarin
 * katalogudur (listGrantable) ve kullanicinin kendi yetkilerini icermez.
 */
export type OrganizationMeResponse = ApiEnvelope & {
  organization?: RawOrganization & { organizationName?: string };
  membership?: { memberType?: string; permissions?: string[] };
};

export const getOrganizationMe = async (): Promise<OrganizationMeResponse | null> => {
  const { data } = await api.get<OrganizationMeResponse>(`${IAM_BASE}/v1/organizations/me`);
  if (data.code !== 'success') return null;
  return data;
};

/** Il/ilce listesi (2026-10-02, M4) — formlarin secicileri. Oturum acmis her kullaniciya acik. */
export const fetchLocations = async (): Promise<TrLocation[]> => {
  const { data } = await api.get<{ locations?: unknown }>(`${IAM_BASE}/v1/locations`);
  return parseLocations(data?.locations);
};
